import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Radar, Menu, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { ThemeToggle } from "@/components/ThemeToggle";


const links = [
  { href: "/#features", label: "Features" },
  { href: "/marketplace", label: "Marketplace" },
  { href: "/about", label: "About" },
  { href: "/#proof", label: "Proof" },
  { href: "/roadmap", label: "Roadmap" },
  { href: "/#pricing", label: "Pricing" },
  { href: "/#faq", label: "FAQ" },
  { href: "/support", label: "Help" },

];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  // Fix hash-anchor scroll on first load
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      // Wait for sections to mount
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/60 border-b border-border/60">
      <div className="container flex items-center justify-between h-16">
        <a href="/" className="flex items-center gap-2 font-display text-lg font-bold">
          <span className="grid place-items-center w-8 h-8 rounded-md bg-primary text-primary-foreground">
            <Radar className="w-4 h-4" />
          </span>
          Trends<span className="text-primary">.</span>Discovery
        </a>
        <nav className="hidden lg:flex items-center gap-8 text-sm text-muted-foreground">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="hover:text-foreground transition-colors">
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle className="hidden lg:inline-flex" />
          <Button variant="ghost" size="sm" asChild className="hidden lg:inline-flex">
            <a href={user ? "/dashboard" : "/auth"}>
              {user ? "Dashboard" : "Sign in"}
            </a>
          </Button>

          {!user && (
            <Button variant="outline" size="sm" asChild className="hidden lg:inline-flex">
              <a href="/auth?mode=signup&redirect=%23pricing">Sign up</a>
            </Button>
          )}
          <Button variant="hero" size="sm" asChild className="hidden lg:inline-flex">
            <a href={user ? "/dashboard" : "/auth?mode=signup&redirect=%23pricing"}>Unlock Trends</a>
          </Button>


          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-border text-foreground"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border/60 bg-background/95 backdrop-blur-xl">
          <nav className="container py-4 flex flex-col gap-1">
            <div className="flex items-center justify-between py-2 px-2 border-b border-border/40">
              <span className="text-sm text-muted-foreground">Appearance</span>
              <ThemeToggle />
            </div>

            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-3 px-2 text-sm text-muted-foreground hover:text-foreground border-b border-border/40"
              >
                {l.label}
              </a>
            ))}
            <a
              href={user ? "/dashboard" : "/auth"}
              onClick={() => setOpen(false)}
              className="py-3 px-2 text-sm text-muted-foreground hover:text-foreground border-b border-border/40"
            >
              {user ? "Dashboard" : "Sign in"}
            </a>
            {!user && (
              <a
                href="/auth?mode=signup&redirect=%23pricing"
                onClick={() => setOpen(false)}
                className="py-3 px-2 text-sm text-muted-foreground hover:text-foreground border-b border-border/40"
              >
                Sign up
              </a>
            )}
            <Button variant="hero" size="sm" asChild className="mt-3">
              <a
                href={user ? "/dashboard" : "/auth?mode=signup&redirect=%23pricing"}
                onClick={() => setOpen(false)}
              >
                Unlock Trends
              </a>
            </Button>


          </nav>
        </div>
      )}
    </header>
  );
};

export default Nav;
